import { FC } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Input } from '../../ui/Input';
import { SecretInput } from '../../ui/SecretInput';
import { AffiliateSettings } from '../../../utils/affiliateSettings';

interface AffiliatesSettingsTabProps {
  affiliateSettings: AffiliateSettings;
  setAffiliateSettings: React.Dispatch<React.SetStateAction<AffiliateSettings>>;
  isSaving: boolean;
  isSaved: boolean;
  testingMarketplace: string | null;
  onSave: () => void;
  onTestConnection: (marketplace: string) => void;
}

export const AffiliatesSettingsTab: FC<AffiliatesSettingsTabProps> = ({
  affiliateSettings,
  setAffiliateSettings,
  isSaving,
  isSaved,
  testingMarketplace,
  onSave,
  onTestConnection,
}) => {
  return (
    <Card id="affiliate-settings">
      <Card.Header
        title={
          <div>
            <h3 className="text-sm font-semibold text-content-main">
              Contas e Tags de Afiliados
            </h3>
            <p className="text-xs text-content-muted">
              Configure os identificadores de cada marketplace para substituição
              e monetização automática
            </p>
          </div>
        }
        action={
          <Button
            variant={isSaved ? 'secondary' : 'primary'}
            size="sm"
            isLoading={isSaving}
            onClick={onSave}
          >
            {isSaved ? 'Salvo' : 'Salvar Tags'}
          </Button>
        }
      />
      <Card.Body className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Shopee */}
          <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <div className="flex items-center justify-between border-b border-surface-border pb-2.5">
              <div>
                <span className="text-xs font-semibold text-content-main">
                  Shopee Afiliados (API Oficial)
                </span>
                <p className="font-mono text-2xs text-content-muted">
                  Open API GraphQL (Oficial)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    affiliateSettings.shopeeAppId &&
                    affiliateSettings.shopeeSecret
                      ? 'online'
                      : 'warning'
                  }
                  className="text-micro"
                >
                  {affiliateSettings.shopeeAppId &&
                  affiliateSettings.shopeeSecret
                    ? 'API Conectada'
                    : 'Não Configurado'}
                </Badge>
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-micro"
                  isLoading={testingMarketplace === 'shopee'}
                  onClick={() => onTestConnection('shopee')}
                  title="Testar conexão com a API da Shopee"
                >
                  Testar
                </Button>
              </div>
            </div>
            <div className="space-y-2.5">
              <Input
                label="Shopee App ID"
                placeholder="Ex: 1000234"
                value={affiliateSettings.shopeeAppId}
                onChange={(e) =>
                  setAffiliateSettings({
                    ...affiliateSettings,
                    shopeeAppId: e.target.value.trim(),
                  })
                }
              />
              <SecretInput
                label="Shopee Secret Key"
                placeholder="Ex: a8f9bc..."
                value={affiliateSettings.shopeeSecret}
                onChange={(e) =>
                  setAffiliateSettings({
                    ...affiliateSettings,
                    shopeeSecret: e.target.value.trim(),
                  })
                }
                helperText="Permite gerar links oficiais s.shopee.com.br e buscar fotos de catálogo"
              />
            </div>
          </div>

          {/* Mercado Livre */}
          <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <div className="flex items-center justify-between border-b border-surface-border pb-2.5">
              <div>
                <span className="text-xs font-semibold text-content-main">
                  Mercado Livre Afiliados
                </span>
                <p className="font-mono text-2xs text-content-muted">
                  Developers API & Matt Word
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    affiliateSettings.mercadolivreAppId ||
                    affiliateSettings.mercadolivreTag
                      ? 'online'
                      : 'warning'
                  }
                  className="text-micro"
                >
                  {affiliateSettings.mercadolivreAppId
                    ? 'API Conectada'
                    : affiliateSettings.mercadolivreTag
                      ? 'Tag Ativa'
                      : 'Não Configurado'}
                </Badge>
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-micro"
                  isLoading={testingMarketplace === 'mercadolivre'}
                  onClick={() => onTestConnection('mercadolivre')}
                  title="Testar conexão com a API do Mercado Livre"
                >
                  Testar
                </Button>
              </div>
            </div>
            <div className="space-y-2.5">
              <Input
                label="Tag / Matt Word (Afiliados)"
                placeholder="Ex: seunome"
                value={affiliateSettings.mercadolivreTag}
                onChange={(e) =>
                  setAffiliateSettings({
                    ...affiliateSettings,
                    mercadolivreTag: e.target.value.trim(),
                  })
                }
                helperText="Injetada como parâmetro ?matt_word= nos links do Mercado Livre"
              />
              <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                <Input
                  label="App ID (Meli)"
                  placeholder="Ex: 81234567..."
                  value={affiliateSettings.mercadolivreAppId}
                  onChange={(e) =>
                    setAffiliateSettings({
                      ...affiliateSettings,
                      mercadolivreAppId: e.target.value.trim(),
                    })
                  }
                />
                <SecretInput
                  label="Secret Key (Meli)"
                  placeholder="Ex: sec_..."
                  value={affiliateSettings.mercadolivreSecretKey}
                  onChange={(e) =>
                    setAffiliateSettings({
                      ...affiliateSettings,
                      mercadolivreSecretKey: e.target.value.trim(),
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Amazon Associados */}
          <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <div className="flex items-center justify-between border-b border-surface-border pb-2.5">
              <div>
                <span className="text-xs font-semibold text-content-main">
                  Amazon Associados
                </span>
                <p className="font-mono text-2xs text-content-muted">
                  Paramétrico & PA-API v5
                </p>
              </div>
              <Badge
                variant={affiliateSettings.amazonTag ? 'online' : 'warning'}
                className="text-micro"
              >
                {affiliateSettings.amazonTag ? 'Ativo' : 'Não Configurado'}
              </Badge>
            </div>
            <Input
              label="Tracking ID / Tag Amazon"
              placeholder="Ex: seunome-20"
              value={affiliateSettings.amazonTag}
              onChange={(e) =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  amazonTag: e.target.value.trim(),
                })
              }
              helperText="Injetada como parâmetro ?tag= nos links amzn.to e amazon.com.br"
            />
          </div>

          {/* Magazine Luiza */}
          <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-4">
            <div className="flex items-center justify-between border-b border-surface-border pb-2.5">
              <div>
                <span className="text-xs font-semibold text-content-main">
                  Magazine Luiza (Parceiro Magalu)
                </span>
                <p className="font-mono text-2xs text-content-muted">
                  Parceiro Magalu / Magazine Você
                </p>
              </div>
              <Badge
                variant={
                  affiliateSettings.magaluPartnerId ? 'online' : 'warning'
                }
                className="text-micro"
              >
                {affiliateSettings.magaluPartnerId
                  ? 'Ativo'
                  : 'Não Configurado'}
              </Badge>
            </div>
            <Input
              label="ID de Parceiro Magalu"
              placeholder="Ex: seunome"
              value={affiliateSettings.magaluPartnerId}
              onChange={(e) =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  magaluPartnerId: e.target.value.trim(),
                })
              }
              helperText="Injetada como parâmetro ?partner_id= nos links magazineluiza"
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
